import { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ShieldAlert, Trash2, CheckCircle2, Mail } from 'lucide-react';
import { Logo, Button } from '../ui';
import legacyAccountService, {
  LegacyAccountError,
  type LegacyLoginResult,
} from '../../services/api/legacyAccountService';

type Step = 'login' | 'confirm' | 'done';

export function DeleteAccount() {
  const { t } = useTranslation();

  const [step, setStep] = useState<Step>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [session, setSession] = useState<LegacyLoginResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await legacyAccountService.login(email, password);
      setSession(result);
      setStep('confirm');
    } catch (err) {
      const message =
        err instanceof LegacyAccountError ? err.message : t('deleteAccount.errors.generic');
      setError(message || t('deleteAccount.errors.login'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!session) return;
    setError(null);
    setLoading(true);
    try {
      const ok = await legacyAccountService.deleteAccount(session.idUsuario, session.token);
      if (ok) {
        setStep('done');
      } else {
        setError(t('deleteAccount.errors.delete'));
      }
    } catch {
      setError(t('deleteAccount.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const dataToDelete = t('deleteAccount.dataList', { returnObjects: true }) as string[];

  return (
    <div className="min-h-screen bg-brand-darkNavy text-white flex flex-col">
      {/* Top bar */}
      <header className="border-b border-brand-lightBlue/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <a href="/" aria-label="BTalentStar">
            <Logo />
          </a>
          <a
            href="/"
            className="inline-flex items-center gap-2 text-sm text-dark-400 hover:text-brand-yellow transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('deleteAccount.back')}
          </a>
        </div>
      </header>

      <main className="flex-1 container mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 flex justify-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-xl"
        >
          {/* Intro */}
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 rounded-2xl bg-accent-rose/10 border border-accent-rose/30 flex items-center justify-center shrink-0">
              <ShieldAlert className="w-6 h-6 text-accent-rose" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold">{t('deleteAccount.title')}</h1>
          </div>
          <p className="text-dark-400 leading-relaxed mb-8">{t('deleteAccount.intro')}</p>

          <div className="relative bg-brand-navy/80 backdrop-blur-xl border border-brand-lightBlue/30 rounded-2xl p-6 md:p-8">
            {step === 'login' && (
              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2" htmlFor="email">
                    {t('deleteAccount.form.email')}
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(ev) => setEmail(ev.target.value)}
                    className="w-full rounded-xl bg-brand-darkNavy border border-brand-lightBlue/40 px-4 py-3 text-white placeholder:text-dark-500 focus:outline-none focus:border-brand-yellow transition-colors"
                    placeholder="tu@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2" htmlFor="password">
                    {t('deleteAccount.form.password')}
                  </label>
                  <input
                    id="password"
                    type="password"
                    required
                    autoComplete="current-password"
                    value={password}
                    onChange={(ev) => setPassword(ev.target.value)}
                    className="w-full rounded-xl bg-brand-darkNavy border border-brand-lightBlue/40 px-4 py-3 text-white placeholder:text-dark-500 focus:outline-none focus:border-brand-yellow transition-colors"
                    placeholder="••••••••"
                  />
                </div>

                {error && (
                  <p className="text-sm text-accent-rose bg-accent-rose/10 border border-accent-rose/30 rounded-xl px-4 py-3">
                    {error}
                  </p>
                )}

                <Button type="submit" variant="primary" fullWidth isLoading={loading}>
                  {t('deleteAccount.form.continue')}
                </Button>
                <p className="text-xs text-dark-500 text-center">
                  {t('deleteAccount.form.loginHint')}
                </p>
              </form>
            )}

            {step === 'confirm' && session && (
              <div className="space-y-6">
                <div className="flex items-center gap-3 text-sm text-dark-300">
                  <Mail className="w-5 h-5 text-brand-yellow shrink-0" />
                  <span>{session.email}</span>
                </div>

                <div>
                  <h2 className="font-bold text-white mb-3">{t('deleteAccount.confirm.heading')}</h2>
                  <ul className="space-y-2">
                    {dataToDelete.map((item) => (
                      <li key={item} className="flex items-start gap-2 text-sm text-dark-400">
                        <span className="text-accent-rose mt-1">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <p className="text-sm text-dark-400 bg-accent-rose/10 border border-accent-rose/30 rounded-xl px-4 py-3">
                  {t('deleteAccount.confirm.warning')}
                </p>

                {error && (
                  <p className="text-sm text-accent-rose bg-accent-rose/10 border border-accent-rose/30 rounded-xl px-4 py-3">
                    {error}
                  </p>
                )}

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    variant="ghost"
                    fullWidth
                    onClick={() => {
                      setStep('login');
                      setSession(null);
                      setPassword('');
                      setError(null);
                    }}
                  >
                    {t('deleteAccount.confirm.cancel')}
                  </Button>
                  <Button
                    variant="primary"
                    fullWidth
                    isLoading={loading}
                    icon={<Trash2 />}
                    onClick={handleDelete}
                    className="!bg-accent-rose !text-white !border-accent-rose/50 !shadow-accent-rose/25"
                  >
                    {t('deleteAccount.confirm.delete')}
                  </Button>
                </div>
              </div>
            )}

            {step === 'done' && (
              <div className="text-center space-y-4 py-4">
                <div className="w-16 h-16 rounded-full bg-brand-yellow/10 border border-brand-yellow/30 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-brand-yellow" />
                </div>
                <h2 className="text-xl font-bold text-white">{t('deleteAccount.done.heading')}</h2>
                <p className="text-dark-400">{t('deleteAccount.done.message')}</p>
                <Button variant="secondary" onClick={() => (window.location.href = '/')}>
                  {t('deleteAccount.done.home')}
                </Button>
              </div>
            )}
          </div>

          <p className="text-xs text-dark-500 mt-6 text-center">
            {t('deleteAccount.support')}{' '}
            <a href="mailto:contact@btalentstar.com" className="text-brand-yellow hover:underline">
              contact@btalentstar.com
            </a>
          </p>
        </motion.div>
      </main>
    </div>
  );
}

export default DeleteAccount;
